


import {
  computed,
  defineComponent,
  PropType,
  reactive,
  ref,
  watch,
  nextTick,
} from '@vue/composition-api';
import { ModalPhotoWithEdit } from '@/models/photoModal';
import useImageZoom from '@/composables/useImageZoom';
import draggable from 'vuedraggable';
import { NO_PHOTO_IMAGE } from '@/consts/externalReports/hokkaidoAirport';
import { useRequestGuard } from '@/composables/useRequestGuard';

interface PhotoGroup {
  key: string;
  photos: ModalPhotoWithEdit[];
}

interface PhotoModalState {
  commentOrg: string;
  photos: ModalPhotoWithEdit[];
  photoGroups: PhotoGroup[];
  dragPhoto: ModalPhotoWithEdit | null;
  shouldShowPhotoModal: boolean;
  showConfirmDeleteModal: boolean;
  showEditClearConfirmModal: boolean;
  selectedPhoto: ModalPhotoWithEdit | null;
  showErrorModal: boolean;
  errorModalMsg: string;
  pendingAction: 'close' | 'switch' | 'add' | null;
  pendingPhoto: ModalPhotoWithEdit | null;
}

export default defineComponent({
  props: {
    title: {
      type: String,
      required: true,
    },
    photos: {
      type: Array as PropType<ModalPhotoWithEdit[]>,
      required: true,
    },
    enableZoom: {
      type: Boolean,
      default: false,
    },
    maxPendingPhotosCount: {
      type: Number,
      default: 50,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    draggable,
  },
  setup(props, { emit }) {
    const photoList = ref<HTMLElement | null>(null);
    const imageFileInput = ref<HTMLInputElement | null>(null);
    const state = reactive<PhotoModalState>({
      commentOrg: '',
      photos: [],
      photoGroups: [],
      dragPhoto: null,
      shouldShowPhotoModal: true,
      showConfirmDeleteModal: false,
      showEditClearConfirmModal: false,
      selectedPhoto: null,
      showErrorModal: false,
      errorModalMsg: '',
      pendingAction: null,
      pendingPhoto: null,
    });
    const {
      zoomIn,
      zoomOut,
      resetZoom,
      canZoomIn,
      canZoomOut,
      canDrag,
      startDrag,
      onDrag,
      endDrag,
      panZoomStyle,
    } = useImageZoom({ enableZoom: props.enableZoom });
    const { isRequesting } = useRequestGuard();
    const photoIndex = computed<number>(() => {
      if (!state.selectedPhoto) {
        return -1;
      }
      return state.photos.indexOf(state.selectedPhoto);
    });
    const canSaveAdditionalPhoto = computed<boolean>(() => {
      return state.photos.length <= props.maxPendingPhotosCount;
    });
    const isHead = computed<boolean>(() => {
      return photoIndex.value <= 0;
    });
    const isTail = computed<boolean>(() => {
      return photoIndex.value === state.photos.length - 1;
    });
    const photoType = computed<string>(() => {
      return props.title.replace('写真', '');
    });
    const dragHandle = computed<string>(() => {
      return props.readonly ? '.nothing' : '.image';
    });
    const commentProxy = computed({
      get: () => state.selectedPhoto ? state.selectedPhoto.comment : '',
      set: (val) => {
        if (state.selectedPhoto) {
          state.selectedPhoto.comment = val;
        }
      },
    });

    const locationSignature = (photo: ModalPhotoWithEdit): string => {
      if (photo?.lon > 0 && photo?.lat > 0) {
        return `${photo.lon}:${photo.lat}`;
      }
      return `no-location:${photo.id}`;
    };
    const buildPhotoGroups = (photos: ModalPhotoWithEdit[]): PhotoGroup[] => {
      const photosByLocation = new Map<string, ModalPhotoWithEdit[]>();
      for (const photo of photos) {
        const key = locationSignature(photo);
        const groupPhotos = photosByLocation.get(key) ?? [];
        groupPhotos.push(photo);
        photosByLocation.set(key, groupPhotos);
      }
      return [...photosByLocation.entries()].map(([key, photos]) => ({
        key,
        photos,
      }));
    };
    const hasMultiplePhotoGroups = (): boolean => state.photoGroups.length > 1;
    const selectPhoto = (
      photo: ModalPhotoWithEdit | null,
      options: { resetZoom?: boolean } = {},
    ): void => {
      state.selectedPhoto = photo;
      state.commentOrg = photo?.comment ?? '';
      if (options.resetZoom) {
        resetZoom();
      }
    };
    const isCommentChanged = (): boolean => {
      return !!state.selectedPhoto && state.commentOrg !== state.selectedPhoto.comment;
    };
    const queuePendingActionIfCommentChanged = (
      action: PhotoModalState['pendingAction'],
      pendingPhoto: ModalPhotoWithEdit | null = null,
    ): boolean => {
      if (!isCommentChanged()) {
        return false;
      }

      state.pendingAction = action;
      state.pendingPhoto = pendingPhoto ?? null;
      state.showEditClearConfirmModal = true;
      state.shouldShowPhotoModal = false;
      return true;
    };
    const closePhotoModal = () => {
      if (queuePendingActionIfCommentChanged('close')) return;

      emit('close-photo-modal');
    };
    const isSelectedPhoto = (photo: ModalPhotoWithEdit): boolean => {
      return photo.id === state.selectedPhoto?.id;
    };
    const onClickPhoto = (photo: ModalPhotoWithEdit): void => {
      if (queuePendingActionIfCommentChanged('switch', photo)) return;

      selectPhoto(photo, { resetZoom: true });
    };
    const getPhotoImage = (photo: ModalPhotoWithEdit): string => {
      return photo.savedImage || NO_PHOTO_IMAGE;
    };
    const moveSelection = (offset: number): void => {
      const targetPhoto = state.photos[photoIndex.value + offset];
      if (!targetPhoto) return;
      if (queuePendingActionIfCommentChanged('switch', targetPhoto)) return;

      selectPhoto(targetPhoto, { resetZoom: true });
    };
    const showPrevious = (): void => moveSelection(-1);
    const showNext = (): void => moveSelection(1);
    const showConfirmDeleteModal = (photo: ModalPhotoWithEdit): void => {
      selectPhoto(photo);
      state.showConfirmDeleteModal = true;
      state.shouldShowPhotoModal = false;
    };
    const onGroupOrderChange = (): void => {
      if (!state.selectedPhoto) return;

      const changedOrders: Array<{ id: number; order: number }> = [];
      const orderedPhotos = state.photoGroups.flatMap(group => group.photos);

      orderedPhotos.forEach((photo, index) => {
        const order = index + 1;
        if (photo.order !== order) {
          updatePhotoTitle(photo, order);
          photo.order = order;
          changedOrders.push({ id: photo.id, order });
        }
      });
      state.photos = [...orderedPhotos];
      refreshPhotoGroups();

      if (changedOrders.length > 0) {
        emit('update-photos-order', state.selectedPhoto.id, changedOrders);
      }
    };
    const onPhotoDragStart = (event: any): void => {
      const photo = state.photos[event.oldIndex];
      state.dragPhoto = photo ?? null;
    };
    const onPhotoOrderChange = (event: any): void => {
      const { oldIndex, newIndex } = event;
      if (!state.dragPhoto || oldIndex === newIndex || !state.selectedPhoto) {
        state.dragPhoto = null;
        return;
      }

      const slots = [...state.photos].sort((a, b) => a.order - b.order);
      const slotOrders = slots.map(photo => photo.order);
      const originalOrderMap = new Map(slots.map(photo => [photo.id, photo.order]));
      const orderedPhotos = [...state.photos];
      state.photos.splice(0, state.photos.length, ...orderedPhotos);

      const newOrderArray = orderedPhotos.map((photo, index) => ({
        id: photo.id,
        order: slotOrders[index],
      }));
      const changedOrders = newOrderArray.filter(({ id, order }) => originalOrderMap.get(id) !== order);

      state.dragPhoto = null;
      if (changedOrders.length > 0) {
        emit('update-photos-order', state.selectedPhoto.id, changedOrders);
      }
    };
    const onImageFileAttach = async(evt: Event): Promise<void> => {
      const fileList = (evt.target as HTMLInputElement).files;
      if (!fileList || fileList.length === 0) return;
      const file = fileList[0];

      if (!canSaveAdditionalPhoto.value) {
        state.errorModalMsg = '添付画像の最大数は' + props.maxPendingPhotosCount + '枚です。';
        state.showErrorModal = true;
        state.shouldShowPhotoModal = false;
        return;
      }

      emit('save-image-candidate', file, photoType.value);
      state.shouldShowPhotoModal = true;
    };
    const onAddPhotoClick = (): void => {
      if (queuePendingActionIfCommentChanged('add')) return;

      const inputEl = imageFileInput.value;
      if (inputEl) {
        inputEl.click();
      }
    };
    const onCommentInput = (): void => {
      if (!state.selectedPhoto) return;

      state.commentOrg = state.selectedPhoto.comment;
      emit('update-photo-comment', state.selectedPhoto.id, state.selectedPhoto.comment);
    };
    const doDeletePhoto = (): void => {
      const selectedPhoto = state.selectedPhoto;
      if (!selectedPhoto) return;

      emit('delete-photo', selectedPhoto.id);
      state.showConfirmDeleteModal = false;
      if (state.photos.filter(p => p.id !== selectedPhoto.id).length <= 0) emit('close-photo-modal');

      state.shouldShowPhotoModal = true;
    };
    const handleDismissConfirmDeleteModal = (): void => {
      state.showConfirmDeleteModal = false;
      state.shouldShowPhotoModal = true;
    };
    const handleDismissErrorModal = (): void => {
      state.showErrorModal = false;
      state.shouldShowPhotoModal = true;
    };
    const handleDismissEditClearModal = (): void => {
      state.showEditClearConfirmModal = false;
      state.shouldShowPhotoModal = true;
      state.pendingAction = null;
      state.pendingPhoto = null;
    };
    const closeEditModal = (): void => {
      if (state.selectedPhoto) {
        state.selectedPhoto.comment = state.commentOrg;
      }
      state.shouldShowPhotoModal = true;
      state.showEditClearConfirmModal = false;
      const action = state.pendingAction;
      const pendingPhoto = state.pendingPhoto;
      state.pendingAction = null;
      state.pendingPhoto = null;
      if (action === 'close') {
        emit('close-photo-modal');
        return;
      }

      if (action === 'switch' && pendingPhoto) {
        selectPhoto(pendingPhoto, { resetZoom: true });
      }

      if (action === 'add') {
        nextTick(() => {
          const inputEl = imageFileInput.value;
          if (inputEl) {
            inputEl.click();
          }
        });
      }
    };
    const refreshPhotoGroups = (): void => {
      state.photoGroups = buildPhotoGroups(state.photos);
    };
    const updatePhotoTitle = (photo: ModalPhotoWithEdit, order: number): void => {
      const suffix = photo.photoTitle.replace(/^\d+\.\s*/, '');
      photo.photoTitle = suffix ? `${order}. ${suffix}` : `${order}.`;
    };

    watch(() => props.photos, (newPhotos: ModalPhotoWithEdit[]) => {
      const prevSelectedId = state.selectedPhoto?.id ?? null;
      state.photos = newPhotos.map(p => ({ ...p }));
      refreshPhotoGroups();
      if (state.photos.length === 0) {
        selectPhoto(null);
        return;
      }
      const nextPhoto = prevSelectedId
        ? state.photos.find(p => p.id === prevSelectedId) ?? state.photos[0]
        : state.photos[0];
      selectPhoto(nextPhoto);
    }, { immediate: true });

    return {
      state,
      photoList,
      imageFileInput,
      canZoomIn,
      canZoomOut,
      canDrag,
      commentProxy,
      dragHandle,
      isHead,
      isTail,
      isRequesting,
      panZoomStyle,
      closePhotoModal,
      closeEditModal,
      isCommentChanged,
      getPhotoImage,
      endDrag,
      doDeletePhoto,
      handleDismissConfirmDeleteModal,
      handleDismissEditClearModal,
      handleDismissErrorModal,
      hasMultiplePhotoGroups,
      isSelectedPhoto,
      onDrag,
      onClickPhoto,
      onPhotoDragStart,
      onPhotoOrderChange,
      onAddPhotoClick,
      onImageFileAttach,
      onGroupOrderChange,
      onCommentInput,
      startDrag,
      showPrevious,
      showConfirmDeleteModal,
      showNext,
      zoomIn,
      zoomOut,
    };
  },
});
